import React from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { useTranslation } from 'react-i18next';
import { CONTENT_SHAPE } from '../../constants/contentColors';
import { ZERO_ICONS } from '../../constants/zeroIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { contentBorders } from '../../theme/createContentTheme';
import type { BranchOperationalRow } from '../../types/dashboard.type';
import { completionRatePercent, type OrderCompletion } from '../../utils/branchOrders.helpers';
import { toLngLat } from '../../utils/geo';
import MaterialList from './MaterialList';

export const UNASSIGNED_SITE_ID = '__unassigned__';

interface BranchTableProps {
  rows: BranchOperationalRow[];
  /** Organisation total collected (kg) for each branch's share. */
  totalCollectedKg: number;
  expandedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  selectedId: string | null;
  onShowOnMap: (id: string) => void;
  /** Orders per branch (completed / total); null when the order list is unavailable. */
  orderCompletion: ReadonlyMap<string, OrderCompletion> | null;
  orderCompletionLoading?: boolean;
}

/**
 * Branches as one scannable table: collected mass, share of the organisation,
 * completion by mass and the order completion rate. A row expands to the branch's
 * materials. Unassigned waste closes the list, muted, and never sits on the map.
 */
const BranchTable: React.FC<BranchTableProps> = ({
  rows,
  totalCollectedKg,
  expandedIds,
  onToggle,
  selectedId,
  onShowOnMap,
  orderCompletion,
  orderCompletionLoading = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const borders = contentBorders(theme);
  const ordered = [...rows].sort((a, b) => {
    const ua = a.collection_site_id === UNASSIGNED_SITE_ID ? 1 : 0;
    const ub = b.collection_site_id === UNASSIGNED_SITE_ID ? 1 : 0;
    return ua - ub || (b.total_waste_kg ?? 0) - (a.total_waste_kg ?? 0);
  });

  return (
    // Sits flush inside the Branches card — no frame of its own.
    <TableContainer>
      <Table size="small" aria-label={t('branchAnalysis.table.title')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('branchAnalysis.table.branch')}</TableCell>
            <TableCell align="right">{t('branchAnalysis.table.collected')}</TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
              {t('branchAnalysis.table.share')}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
              {t('branchAnalysis.table.completion')}
            </TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
              {t('branchAnalysis.table.completionRate')}
            </TableCell>
            <TableCell sx={{ width: 44 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {ordered.map((row) => {
            const id = row.collection_site_id;
            const unassigned = id === UNASSIGNED_SITE_ID;
            const expanded = expandedIds.has(id);
            const selected = selectedId === id;
            const name = unassigned
              ? t('branchAnalysis.unassignedBranch')
              : row.name?.trim() || t('branchAnalysis.unnamedBranch');
            const located = !unassigned && toLngLat(row.latitude, row.longitude) !== null;
            const materials = (row.materials ?? []).filter((m) => m.total_kg > 0);
            const share = totalCollectedKg > 0 ? (row.total_waste_kg ?? 0) / totalCollectedKg : null;
            // No collections at all: a 0% completion rate would read as "all failed".
            const idle = !(row.total_waste_kg > 0) && !(row.waste_diversion_rate_percent > 0);
            const completion = idle ? '—' : fmt.withUnit('percent', row.waste_diversion_rate_percent);
            const orders = orderCompletion?.get(id);
            const rate = completionRatePercent(orders);
            const completionRate = rate === null ? '—' : fmt.withUnit('percent', rate);
            const completionRateTitle = orders && orders.total > 0 ? `${orders.completed} / ${orders.total}` : undefined;
            return (
              <React.Fragment key={id}>
                <TableRow
                  hover
                  id={`branch-row-${id}`}
                  onClick={() => onToggle(id)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : undefined,
                    '& > td': { borderBottom: expanded ? 0 : undefined },
                    opacity: unassigned ? 0.8 : 1,
                  }}
                >
                  <TableCell sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      <Box
                        aria-hidden
                        sx={{
                          width: 30,
                          height: 30,
                          flexShrink: 0,
                          display: 'grid',
                          placeItems: 'center',
                          borderRadius: `${CONTENT_SHAPE.sm}px`,
                          bgcolor: unassigned ? 'action.hover' : alpha(theme.palette.primary.main, 0.08),
                          color: unassigned ? 'text.secondary' : 'primary.main',
                        }}
                      >
                        {unassigned ? <ZERO_ICONS.insights sx={{ fontSize: 17 }} /> : <ZERO_ICONS.branch sx={{ fontSize: 17 }} />}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {name}
                        </Typography>
                        {row.branch_contact ? (
                          <Typography variant="caption" color="text.secondary" component="p">
                            <bdi dir="ltr">{row.branch_contact}</bdi>
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <bdi>{fmt.withUnit('mass_kg', row.total_waste_kg, { scaleMass: true })}</bdi>
                  </TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, color: 'text.secondary' }}>
                    <bdi>{fmt.share(share)}</bdi>
                  </TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <bdi>{completion}</bdi>
                  </TableCell>
                  <TableCell align="right" title={completionRateTitle} sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {orderCompletionLoading ? (
                      <Skeleton variant="text" width={36} sx={{ display: 'inline-block' }} />
                    ) : (
                      <bdi>{completionRate}</bdi>
                    )}
                  </TableCell>
                  <TableCell sx={{ px: 0.5 }}>
                    <IconButton
                      size="small"
                      aria-expanded={expanded}
                      aria-controls={`branch-detail-${id}`}
                      aria-label={expanded ? t('branchAnalysis.table.hideDetails') : t('branchAnalysis.table.showDetails')}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggle(id);
                      }}
                    >
                      <ExpandMoreRounded
                        fontSize="small"
                        sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 200ms ease' }}
                      />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}>
                    <Collapse in={expanded} unmountOnExit>
                      <Box
                        id={`branch-detail-${id}`}
                        sx={{
                          display: 'grid',
                          gap: 2,
                          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
                          px: 2,
                          pb: 2,
                          pt: 0.5,
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
                            {t('branchAnalysis.table.materials')}
                          </Typography>
                          {materials.length > 0 ? (
                            <MaterialList breakdown={materials} ariaLabel={`${name} · ${t('branchAnalysis.table.materials')}`} />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('branchAnalysis.table.noMaterials')}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'grid', gap: 1, alignContent: 'start' }}>
                          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 3, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('branchAnalysis.table.completion')}:{' '}
                              <Box component="bdi" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                {completion}
                              </Box>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('branchAnalysis.table.completionRate')}:{' '}
                              <Box component="bdi" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                {orderCompletionLoading ? '…' : completionRate}
                              </Box>
                            </Typography>
                          </Box>
                          {unassigned ? (
                            <Typography variant="body2" color="text.secondary">
                              {t('branchAnalysis.table.unassignedHint')}
                            </Typography>
                          ) : located ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              startIcon={<ZERO_ICONS.site fontSize="small" />}
                              onClick={() => onShowOnMap(id)}
                              sx={{ justifySelf: 'start' }}
                            >
                              {t('branchAnalysis.table.showOnMap')}
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('branchAnalysis.locationNotSet')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BranchTable;
